// src/widgets/LeagueStandings/index.jsx
import { useMemo } from 'react';
import styled from 'styled-components/macro';
import theme from 'styled-theming';

// components
import LeagueHeader from '@components/LeagueHeader';
import Spring from '@components/Spring';
import TeamScoreRow, { StyledRow } from '@components/TeamScoreRow';

// hooks
import { useThemeProvider } from '@contexts/themeContext';

// assets (fallback header image)
import english_premier from '@assets/clubs/english_premier.webp';

const TableHeader = styled(StyledRow)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${theme('theme', {
    light: 'var(--body)',
    dark: '#414D55',
  })};
  color: var(--btn-text) !important;

  &.ltr { padding: 0 4px 0 10px; }
  &.rtl { padding: 0 10px 0 4px; }

  div {
    background: ${theme('theme', {
      light: 'var(--body)',
      dark: '#414D55',
    })};
  }

  .points { margin-right: 4px; }
`;

const normalizeTable = (rows) =>
  (Array.isArray(rows) ? rows : []).map((r) => {
    const wins = r.wins ?? r.W ?? r.w ?? 0;
    const draws = r.draws ?? r.D ?? r.d ?? 0;
    const losses = r.losses ?? r.L ?? r.l ?? 0;
    const pts = r.points ?? r.pts ?? wins * 3 + draws;

    const logo = r.logo ?? r.image ?? r.teamLogo ?? null;

    return {
      id: r.id,
      name: r.name ?? r.teamName ?? '—',
      logo,            // ensure TeamScoreRow can render a crest
      image: logo,     // provide both keys (compat)
      wins,
      draws,
      losses,
      pts,
      ranking: r.ranking ?? r.rank ?? 9999,
      gd: r.difference ?? r.gd ?? 0,
      gf: r.scored ?? r.goals ?? 0,
      ga: r.against ?? 0,
    };
  })
  // primary sort: ranking asc, then pts desc, then GD desc
  .sort((a, b) => (a.ranking - b.ranking) || (b.pts - a.pts) || (b.gd - a.gd));

const LeagueStandings = ({ table = [], leagueName = 'League', loading = false, headerImage }) => {
  const { direction } = useThemeProvider();

  const tableData = useMemo(() => normalizeTable(table), [table]);

  return (
    <Spring className="card d-flex flex-column g-20 card-padded">
      <LeagueHeader
        title={<>{leagueName}</>}
        img={headerImage || english_premier}
        variant="compact"
      />

      <div className="d-flex flex-column g-4">
        <TableHeader className={`label h6 ${direction}`}>
          <span className="flex-1">Club</span>
          <div className="points">
            <span>W</span>
            <span>D</span>
            <span>L</span>
          </div>
          <span>PTS</span>
        </TableHeader>

        <div className="d-flex flex-column g-1">
          {loading && tableData.length === 0
            ? Array.from({ length: 10 }).map((_, i) => (
                <TeamScoreRow key={`skeleton-${i}`} data={{ name: '—', logo: null, wins: 0, draws: 0, losses: 0, pts: 0 }} index={i} variant="league" loading />
              ))
            : tableData.map((item, index) => (
                <TeamScoreRow
                  key={item.id || `${item.name}-${index}`}
                  data={item}
                  index={index}
                  variant="league"
                />
              ))}
        </div>
      </div>
    </Spring>
  );
};

export default LeagueStandings;
